import { useCallback, useEffect, useState } from "react";
import { FormAlert } from "@NP-Webber/crm-kit";
import { formatShekels } from "../../api/financeApi";
import {
  nedarimApi,
  type NedarimMovement,
} from "../../api/nedarimApi";
import { toClientApiError } from "../../api/httpClient";

export type CustomerPaymentsPanelProps = {
  customerId: string;
};

const STATUS_LABELS: Record<string, string> = {
  approved: "אושר",
  success: "אושר",
  paid: "שולם",
  failed: "נכשל",
  pending: "ממתין",
  cancelled: "בוטל",
};

const KIND_LABELS: Record<string, string> = {
  transaction: "עסקה",
  charge: "חיוב",
  mandate: "הו״ק",
  siruv: "סירוב",
};

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function formatPeriod(period: string | null): string {
  if (!period) return "—";
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  return `${match[2]}/${match[1]}`;
}

export function CustomerPaymentsPanel({ customerId }: CustomerPaymentsPanelProps) {
  const [items, setItems] = useState<NedarimMovement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const rows = await nedarimApi.listMovements({ customerId });
      setItems(rows.filter((row) => row.kind !== "mandate"));
    } catch (err) {
      setError(toClientApiError(err).message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <p className="related-panel__empty">טוען תשלומים…</p>;
  }

  if (error) {
    return <FormAlert severity="error">{error}</FormAlert>;
  }

  if (items.length === 0) {
    return <p className="related-panel__empty">אין תשלומים עדיין.</p>;
  }

  return (
    <div className="related-panel">
      <div style={{ overflow: "auto" }}>
        <table className="related-panel__table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>תאריך</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>סוג</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>סכום</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>חודש גבייה</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>אמצעי</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>מזהה עסקה</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>אסמכתא</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} style={{ borderTop: "1px solid var(--ck-border)" }}>
                <td style={{ padding: "8px 10px" }}>{row.occurredOn ?? "—"}</td>
                <td style={{ padding: "8px 10px" }}>
                  {KIND_LABELS[row.kind] ?? row.kind}
                  {row.groupe ? ` · ${row.groupe}` : ""}
                </td>
                <td style={{ padding: "8px 10px", fontWeight: 600 }}>
                  {formatShekels(row.amountAgorot)} ₪
                </td>
                <td style={{ padding: "8px 10px" }}>{formatPeriod(row.billingPeriod)}</td>
                <td style={{ padding: "8px 10px" }}>{row.channel || "—"}</td>
                <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 12 }}>
                  {row.nedarimTransactionId ?? "—"}
                </td>
                <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 12 }}>
                  {row.nedarimKevaId ?? "—"}
                </td>
                <td style={{ padding: "8px 10px" }}>{statusLabel(row.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
